import type { CreditResult } from './credit';

/** 当前登录周期的共享积分查询事实；故障可保留旧值，鉴权变化必须清空。 */
export interface CreditQuerySnapshot<T> {
  data: T | null;
  status: CreditResult<T>['status'] | 'idle';
  loading: boolean;
  updatedAt: number | null;
}

/** 通过依赖注入隔离浏览器/AuthService，便于验证订阅与异步响应的生命周期。 */
export interface CreditQuerySource<T> {
  /** 返回仅存于内存的登录周期身份；未登录为 null。 */
  identity: () => string | null;
  /** 查询当前账号的权威积分总览，不触发模型调用或支付。 */
  read: () => Promise<CreditResult<T>>;
  /** 注册焦点恢复、付款和轮次结束等刷新通知，返回释放函数。 */
  onRefresh: (listener: () => void) => () => void;
  /** 注册身份变化通知，返回释放函数。 */
  onIdentityChange: (listener: () => void) => () => void;
}

/**
 * 创建只在有页面订阅时存活的共享积分查询状态。
 * 多个组件共享一次初始读取；显式刷新以最新请求为准，注销/卸载使旧响应失效。
 */
export function createCreditQueryStore<T>(source: CreditQuerySource<T>) {
  const EMPTY: CreditQuerySnapshot<T> = { data: null, status: 'idle', loading: false, updatedAt: null };
  let snapshot = EMPTY;
  let identity: string | null = null;
  let sequence = 0;
  let cleanup: (() => void) | undefined;
  const listeners = new Set<() => void>();

  /** 发布不可变快照；useSyncExternalStore 在没有变化时读取同一引用。 */
  const publish = (next: CreditQuerySnapshot<T>): void => {
    snapshot = next;
    listeners.forEach(listener => listener());
  };

  /** 获取平台最新事实，仅允许当前订阅周期、身份和请求序号写回。 */
  const refresh = async (): Promise<void> => {
    if (!listeners.size) return;
    const requestIdentity = source.identity();
    if (identity !== requestIdentity) {
      identity = requestIdentity;
      snapshot = EMPTY;
    }
    const request = ++sequence;
    if (requestIdentity === null) {
      publish({ ...EMPTY, status: 'unauthenticated' });
      return;
    }
    publish({ ...snapshot, loading: true });
    let result: CreditResult<T>;
    try { result = await source.read(); }
    catch { result = { status: 'unavailable', data: null }; }
    if (!listeners.size || request !== sequence || source.identity() !== requestIdentity) return;
    if (result.status === 'ready') {
      publish({ data: result.data, status: 'ready', loading: false, updatedAt: Date.now() });
    } else {
      publish({ ...(result.status === 'unavailable' ? snapshot : EMPTY), status: result.status, loading: false });
    }
  };

  /** 账号变化同步清空旧数据；普通资料更新不重复查询积分。 */
  const onIdentityChange = (): void => {
    if (source.identity() !== identity) void refresh();
  };

  /** 订阅共享状态；最后一个组件离开时释放监听及数据，不跨登录周期缓存。 */
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    if (listeners.size === 1) {
      const offIdentity = source.onIdentityChange(onIdentityChange);
      const offRefresh = source.onRefresh(() => { void refresh(); });
      cleanup = () => { offIdentity(); offRefresh(); };
      void refresh();
    } else {
      onIdentityChange();
    }
    return () => {
      listeners.delete(listener);
      if (!listeners.size) {
        cleanup?.(); cleanup = undefined;
        sequence++; identity = null; snapshot = EMPTY;
      }
    };
  };

  return {
    subscribe,
    refresh,
    /** 身份先于 React 提交变化时也不能短暂暴露旧账号数据。 */
    getSnapshot: (): CreditQuerySnapshot<T> => source.identity() === identity ? snapshot : EMPTY,
  };
}
